/**
 * Price lists — the Price Book (T387 M5+M6, rates-386 D1 base + D3's queue
 * as the composed cover). A shelf of covers, one list open at a time, ONE row
 * grammar everywhere: tap a price to change it (with a required reason), gold
 * means provisional, ratify with one note, per-cell history. The "Needs a
 * decision" cover renders only when the queue is non-empty and sorts first.
 *
 * ⚠️ Routing WITHIN this screen is local useState. The stronger claim that used
 * to sit here — "no state.ts / ScreenId / Rail changes" — was a rule about the
 * three quiet entries, and T928 slice 1 retired it by promoting two of them to
 * their own ScreenIds. What survives is narrower and still worth keeping: the
 * price book's own sub-views (a rate list, add-ons, the queue, a promote form,
 * the account book) are local `View` members, because they are places INSIDE
 * one price book rather than destinations of their own.
 */
import { useEffect, useState } from 'preact/hooks';
import { slugify } from '../../shared/pricing-flatten.js';
import {
  listRates,
  listRatesQueue,
  type ApiRateRow,
  type ApiQueue,
} from '../lib/actions.js';
import { loadMeta, type Registry } from '../store/registry.js';
import { Banner } from '../components/Banner.js';
import { Shelf } from './admin/Shelf.js';
import { RateList } from './admin/RateList.js';
import { AddonsRules } from './admin/AddonsRules.js';
import { QueueView, queueCardCount, type PromoteSeed } from './admin/QueueView.js';
import { PromoteForm } from './admin/PromoteForm.js';
/* ⭐⭐ T1749 M7 (R5) — `AccountBook` AND ITS FOUR READS LEFT THIS FILE. The book
   is hosted by the Homes tab of `admin/NursingHomes.tsx`, which reads it through
   `lib/account-book-loader.ts` (a NAMED object, never the positional seven-tuple
   this file's `load` used to warn about). The quiet entry below is a
   link-through. */

export type AdminProps = {
  registry: Registry;
  /**
   * ⭐⭐ T1749 M7 (R5) — OPEN THE CUSTOMER ACCOUNT BOOK, WHEREVER IT LIVES.
   *
   * It used to live HERE, as the `accounts` member of this screen's `View` union
   * reached by the quiet entry below. R5 moves it under the Homes tab of Nursing
   * homes ("Its Homes tab IS the account book"), and the cover becomes a
   * link-through: this screen asks, `App.tsx` navigates and preselects the tab.
   * ⛔ REQUIRED, NOT OPTIONAL, and unlike `onOpenCatalog` beside it. The catalog
   * is optional because a build may genuinely have none; the account book always
   * exists, so a mount that forgot to wire this would render a quiet entry that
   * does nothing, which is the lying-control class this tool keeps closing.
   * ⛔ A CALLBACK, NEVER A ScreenId HERE. This screen does not know the rail's
   * names and must not learn one to reach a section of another screen.
   */
  onOpenAccountBook: () => void;
  /**
   * T1749 M5 — opens the component catalog, which is a top-level ScreenId rather
   * than a member of this screen's `View` union.
   * ⛔ IT IS A CALLBACK AND NOT A `View` MEMBER, for the reason T928 records
   * below: the catalog is not a price list, and the rule the rail groups by is
   * WHAT a thing is. It is also not a rail row — `SC-10b` pins the rail at eight
   * items — so the quiet entry on this shelf is where it lands.
   * ⛔ ITS PRESENCE IS THE CONDITION, NOT A CONVENIENCE. `App.tsx` passes it only
   * when the resolved catalog module says the build carries a catalog
   * (`CATALOG_PRESENT`), so `undefined` here means "this build has no catalog" and
   * the shelf renders NO control for it. A link that opens a null screen is the
   * blank-pane class `store/state.ts` warns about and a button Helen would learn
   * nothing from. ⚠️ Optional rather than required so every existing
   * `<Admin registry={…} />` keeps compiling — and that spelling IS the OFF
   * condition, which `catalog.spec.tsx` mounts and grades both ways.
   */
  onOpenCatalog?: () => void;
};

const EMPTY_QUEUE: ApiQueue = { provisional: [], misses: [], evidence: [], suggestions: [] };

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error' }
  /**
   * ⭐ T928 / board 926 — `queueFailed` is the LAST of this screen's reads to
   * stop throwing its failure away. The four account reads were fixed in T698
   * (see the `load` note below: "an empty list had two causes and one
   * appearance"); the queue read still caught to EMPTY_QUEUE, so at the render
   * site a dead read and an empty queue were literally the same value.
   *
   * That mattered only once the cover began rendering at zero. Board 926: the
   * "Needs a decision" cover is the ONLY way into the promote queue and it used
   * to render only when non-empty, so an empty queue made the surface
   * unreachable from the UI entirely. The fix is to render it at zero — and the
   * moment it renders at zero, a FAILED read would render as "Nothing waiting",
   * which is a false reassurance about a queue that may be full.
   *
   * ⛔ It rides HERE, on the `ready` arm, and NOT in `setBook`. That object is
   * pinned as a one-write invariant, and this fact has nothing to do with the
   * account book. Nothing is appended to the seven-entry positional
   * `Promise.all` either — the entry count and the positions are unchanged; only
   * that one entry's catch sentinel moved from EMPTY_QUEUE to null.
   */
  | {
      kind: 'ready';
      rates: ApiRateRow[];
      queue: ApiQueue;
      queueFailed: boolean;
      meta: Record<string, string>;
    };

/**
 * T651 M2 — the customer account book's four reads, ALL FOUR DECLARED HERE in
 * one commit so no later milestone adds a fifth field or a fifth tuple slot.
 *
 * ⛔ THEY ARE A SEPARATE SLICE FROM `LoadState`, AND THAT IS THE FIX FOR A REAL
 * DEAD END rather than a tidiness preference. Hung off `LoadState.ready`, the
 * account book could only render when the RATES read had succeeded — and
 * listRates is deliberately load-bearing, so one rates outage put the whole
 * screen into `{kind:'error'}` and the book became unreachable: no heading, no
 * tabs, and no way back, because the quiet entry that got you there only renders
 * on the shelf. That inverts the sentence this screen's own quiet entry exists
 * for ("the view still loads even when the price reads failed, which is the
 * point"). `Operators` next door is immune because it self-fetches; the book
 * cannot, so its data is tracked independently instead.
 *
 * These four cannot fail into an error branch — the SCREEN still renders — but
 * each one now records WHETHER it failed, and that is the T698 change.
 *
 * ⛔ THE DEGRADE STAYS; ITS CONSEQUENCE GAINS A SURFACE. Catching to empty is
 * still right (see the four-read note in `load` below). What was wrong is that
 * an empty list had two causes and one appearance, so a catalogue of 29 rows
 * whose read had failed said "No destinations on the list yet." The fix is not
 * to stop degrading — it is to stop the degrade being INVISIBLE.
 *
 * ⭐ SO A FAILED READ IS `null`, NOT AN EMPTY LIST, and `failed` below is
 * DERIVED from that rather than tracked alongside it. Two facts that must agree
 * cannot disagree if only one of them is stored. This is the sentinel this very
 * function already uses for `rates` ("rates == null IS the error condition"),
 * and the house rule for every read: "`null` = that read FAILED. Distinct from
 * `[]`, which is a read that SUCCEEDED and found nothing ... Do not collapse
 * the two."
 *
 * ⛔ `loaded` CLOSES THE OTHER HALF — the gap this comment used to record as
 * inherited debt. `EMPTY_BOOK` is indistinguishable from a real empty book, so
 * before the first resolve "nothing captured yet" was a guess. It is now a
 * question the book can answer.
 *
 * ⛔ AND THE FIFTH FIELD IS THE TRUNCATION FACT, PER LIST. All four routes are
 * bounded by a server-side LIMIT and used to cut the list SILENTLY; the flags
 * ride here so AccountBook can say so on the tab that was actually cut. Kept as
 * four booleans rather than one OR because the alternative tells Helen "some
 * rows are missing" while she is looking at a list that is complete.
 */
/* ⭐⭐ T1749 M7 (R5) — `BookState`, `NOTHING_CUT`, `NOTHING_FAILED` and
   `EMPTY_BOOK` MOVED TO `lib/account-book-loader.ts` AS `AccountBookData` AND
   `EMPTY_ACCOUNT_BOOK`, because the Homes tab mounts the same book and two
   copies of a four-read degrade is two places to get it wrong. Every rule they
   carried moved with them, verbatim: a failed read is `null` rather than an
   empty list (T698), a failed read is not a truncated one, and the rows, the
   truncation flags and the failure flags are written as ONE object or not at
   all. Read the loader's header before changing any of them. */

type View =
  | { kind: 'shelf' }
  | { kind: 'list'; service: string; filter?: string }
  | { kind: 'addons' }
  | { kind: 'queue' }
  | { kind: 'promote'; seed: PromoteSeed }
  // T651 M2: the CUSTOMER account book (accounts, destinations, prices). ONE
  // member, reached by the quiet entry below — not a Shelf cover, and not one
  // member per sub-list: the sub-navigation lives inside AccountBook, so the
  // three milestones that fill its children never touch this union.
  //
  // ⭐ T928 slice 1: it is now the ONLY quiet entry. `operators` and `portal`
  // were members here too; Alex ruled the rail should group by WHAT a thing is,
  // and neither a staff roster nor an inbound request queue is a price list, so
  // both were promoted to their own ScreenIds. The account book stays precisely
  // because it IS a price book — a third, separate price store keyed
  // (account_id, destination_id) — so it belongs with the price lists.
  /* ⭐⭐ T1749 M7 (R5) — the `accounts` member is GONE with the render below: the
     book is a section of another screen now and this screen navigates to it
     rather than switching a view. Removing the member is what makes that true by
     construction — a `setView({kind:'accounts'})` is a compile error. */
  ;

export function Admin({ registry, onOpenCatalog, onOpenAccountBook }: AdminProps) {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [view, setView] = useState<View>({ kind: 'shelf' });
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const load = () => {
    // The queue read degrades to empty (the shelf still works without it);
    // the RATES read is load-bearing and keeps its honest error state. The
    // META read degrades to the login-time registry copy (stale-but-present
    // beats "Not set" lies) — fresh meta is what keeps the Add-ons pane
    // honest after a save (T387 rework 1, repro A).
    //
    // ⛔ THE RATES READ IS CAUGHT TO `null` RATHER THAN LEFT TO REJECT, AND THAT
    // IS LOAD-BEARING. `Promise.all` rejects the instant any entry does and
    // DISCARDS the settled values of the rest; caught here, the failure produces
    // the honest `{kind:'error'}` for the price book — `rates == null` IS the
    // error condition, one line down — while the queue and meta reads still
    // arrive.
    //
    // ⭐⭐ T1749 M7 (R5) — THE FOUR ACCOUNT READS ARE NO LONGER IN THIS ARRAY,
    // and neither is the book they fed. ⛔ THE POSITIONAL HAZARD THIS BLOCK USED
    // TO WARN ABOUT WENT WITH THEM: seven look-alike `{id,name,active}` reads
    // destructured by position, where appending a fetch in one milestone and
    // another in a second silently bound `accounts` to the destinations array and
    // still compiled. Three entries remain and they are three different shapes.
    Promise.all([
      listRates().catch(() => null),
      // ⭐ T928 / board 926 — `null`, not EMPTY_QUEUE. Same sentinel rule as the
      // rates read above: a failed read is a DISTINCT value, never a
      // successful-and-empty one.
      listRatesQueue().catch(() => null),
      loadMeta().catch(() => null),
    ])
      .then(([rates, queue, meta]) => {
        setState(rates == null
          ? { kind: 'error' }
          : {
              kind: 'ready',
              rates,
              // The DEGRADE is unchanged — the shelf still renders during a
              // queue outage — but the failure is no longer thrown away.
              queue: queue ?? EMPTY_QUEUE,
              queueFailed: queue == null,
              meta: meta ?? ((registry.options.meta ?? {}) as Record<string, string>),
            });
      })
      .catch(() => setState({ kind: 'error' }));
  };
  // Refetch on every VIEW ENTRY, not just mount (T387 rework 1, repro B): a
  // sibling admin view must never show pre-change data after a write made
  // elsewhere in the section, and queue rows minted while we sat on another
  // view must appear when the shelf/queue is (re)entered. Stale-while-
  // revalidate: the old data stays on screen for the ms the refetch takes.
  // Write paths ALSO invalidate explicitly (onChanged={load}) so freshness
  // never hinges on this dependency alone.
  useEffect(load, [view.kind]);

  // Post-coalesce count so the cover's "N waiting" equals the cards on screen.
  const queueCount = (q: ApiQueue) => queueCardCount(q);

  return (
    <div class="screen-pane screen-pane--wide">
      {view.kind === 'shelf' && <h1 class="screen-title" tabIndex={-1}>Price lists</h1>}

      {notice && <Banner kind={notice.kind}>{notice.text}</Banner>}

      {/* ⛔ T651 M2 — BOTH PRICE-LIST STATUS LINES USED TO BE SUPPRESSED IN THE
          ACCOUNT BOOK, because nothing on that screen read rates, queue or meta
          and "Loading the price list failed" over it told Helen a screen she was
          not looking at was broken. ⭐ T1749 M7 (R5): the book is not a view of
          this screen any more, so the exception has no subject and the guard is
          gone with the `accounts` member. Every remaining view keeps today's
          behaviour exactly, which is what it always did. */}
      {state.kind === 'loading' && (
        <p class="helper-note">Getting the price list…</p>
      )}
      {state.kind === 'error' && (
        <Banner kind="error">
          Loading the price list failed. Try again in a moment.{' '}
          <button type="button" class="btn btn--quiet btn--inline" onClick={load}>Try again</button>
        </Banner>
      )}

      {state.kind === 'ready' && view.kind === 'shelf' && (
        <Shelf
          registry={registry}
          rates={state.rates}
          queueCount={queueCount(state.queue)}
          queueFailed={state.queueFailed}
          onOpen={(service, filter) => { setNotice(null); setView({ kind: 'list', service, filter }); }}
          onOpenAddons={() => { setNotice(null); setView({ kind: 'addons' }); }}
          onOpenQueue={() => { setNotice(null); setView({ kind: 'queue' }); }}
        />
      )}

      {/* T642 M6 (R7). Deliberately NOT another Shelf cover: the shelf is the
          PRICE BOOK and its own header pins its interactive budget (see
          Shelf.tsx — 5 fixed covers plus the decision cover plus a search).
          Accounts are not a price list, so they get their own quiet entry
          beneath it.
          ⛔ BOARD 938 — THAT BUDGET ARGUMENT IS COMPONENT-SCOPED AND THE RULE
          IS NOT. DESIGN.md §1 rule 2 caps "≤ 7 interactive elements in the
          CONTENT AREA", not per file. Shelf.tsx's own count is right about
          Shelf.tsx, but this button and the shelf render into the same
          <main class="content"> on the same `view.kind === 'shelf'`, and the
          shell's Back button is in there too — so the screen is NINE, not
          seven. "A quiet entry rather than a cover" is a good visual-weight
          decision and it is kept; what it does NOT do is leave the budget
          intact. Measured and pinned in test/client/control-budget.spec.tsx as
          tracked debt (T938 R2 (a)) — do not re-argue it here in prose.
          The view still loads even when the price reads failed,
          which is the point — a section has to stay fixable on a screen whose
          rates call is broken.
          ⚠️ T928 note: the ORIGINAL sentence here was about "nobody can sign
          in", because this row also held the operator roster. The roster is a
          rail destination now; the reasoning survives for the account book,
          which is what the row still holds. */}
      {view.kind === 'shelf' && (
        <div class="op-add">
          {/* ⛔ T651 M2 — THE INNER ROW IS NOT DECORATION. `.op-add` sets only a
              margin and a max-width; a <button> is inline-block by default and
              JSX strips the whitespace between two of them, so a second quiet
              entry rendered with its border flush against the first.
              `.admin-add-actions` is the shipped button-row class (AddEntryForm,
              PromoteForm); reusing it costs no CSS.
              ⭐ T928 slice 1 took the row from THREE entries back to ONE, so it
              is a row of one today. It is kept rather than unwrapped because it
              is what makes a future second entry land correctly instead of
              flush against this one — and because the note above is the reason,
              which would otherwise have to be rediscovered. */}
          <div class="admin-add-actions">
            {/* The quiet entry, and the last one. The shelf is the PRICE BOOK
                and its own header pins its interactive budget, so the account
                book gets a quiet entry rather than another cover.
                ⛔ BOARD 938 CORRECTION: "the budget above is untouched and
                Shelf.tsx needs no change at all" is what this used to say, and
                the second half is true while the first half is not. Shelf.tsx
                needs no change; the SCREEN's budget is spent by this very
                button. See the note above and control-budget.spec.tsx.
                ⛔ NOT LABELLED "Accounts". That once read against the operator
                roster sitting beside it; the roster has moved to the rail, but
                the wording stands on its own terms — "Accounts" alone does not
                say WHOSE, and this book is the customer's. A spec pins the
                absence of a button named "Accounts". */}
            <button
              type="button"
              class="btn btn--quiet"
              /* ⭐⭐ T1749 M7 (R5) — A LINK-THROUGH NOW, NOT A SUB-VIEW SWITCH.
                 R5: "Price lists keeps a way through to each account's price list
                 but hosts no account operations; the Customer accounts cover
                 becomes a link-through." The book lives under the Homes tab of
                 Nursing homes, so this entry hands App the request and App does
                 the navigating; this screen learns no ScreenId.
                 ⚠️ THE WORDING AND THE WAY THROUGH ARE UNCHANGED: Price lists,
                 this entry, then the Agreed prices strip grouped per account, as
                 it always was. What changed is where it lands. */
              onClick={() => { setNotice(null); onOpenAccountBook(); }}
            >
              Customer accounts &amp; prices
            </button>
            {/* ⭐ T1749 M5 — THE COMPONENT CATALOG'S ENTRY, and it is the SECOND
                quiet entry this row has ever held, which is exactly what the note
                above says the row was kept for.
                ⛔ IT RENDERS ONLY WHERE THERE IS A CATALOG TO OPEN. `App.tsx`
                withholds the opener on a build that resolved the OFF module, so a
                shipped shelf carries no control onto a null screen — the blank-pane
                class `store/state.ts` warns about, and a button Helen would learn
                nothing from (§1's Helen-test). The condition is the PROP, never a
                second env read: `src/client` has none by design.
                ⛔ ITS WORDING IS STILL NOT THE CATALOG'S HEADING, and that is load-
                bearing rather than a style choice: `scripts/check-catalog.mjs`
                greps the BUILT artefacts for the catalog's HEADING, and THIS FILE
                ships in both postures even though the button does not. Name it the
                heading and the OFF leg reds on a marker this source put there.
                ⛔ THE SAME GOES FOR THIS COMMENT — `client.js.map` embeds this
                file's source verbatim, so the marker strings are named indirectly
                here on purpose (measured: the first draft quoted them and reded the
                OFF leg). `catalog-off.tsx`'s header records the rule.
                ⛔ BOARD 938: this button spends one more of the shelf SCREEN's
                interactive budget WHERE IT RENDERS. The `Price book shelf` DEBT row
                names the condition it was measured in — see control-budget.spec.tsx. */}
            {onOpenCatalog !== undefined && (
              <button
                type="button"
                class="btn btn--quiet"
                onClick={() => { setNotice(null); onOpenCatalog(); }}
              >
                Design components
              </button>
            )}
          </div>
        </div>
      )}

      {/* ⛔ GATED ON THE VIEW ALONE, NOT like the three price-list children
          below. Adding `state.kind === 'ready'` here makes the book unreachable
          during a RATES outage — and since the heading and the quiet-entry row
          are both shelf-only, it takes the way back out with it. The book reads
          none of rates/queue/meta.
          ⭐ T928 slice 1: <Operators> and <PortalRequests> used to sit directly
          above under the same rule, and are now rail screens. Their half of that
          rule got STRONGER, not weaker — as their own destinations they cannot
          be gated on this screen's rates read at all. */}
      {/* ⭐⭐ T1749 M7 (R5) — THE ACCOUNT BOOK NO LONGER RENDERS HERE. R5: "Price
          lists keeps a way through to each account's price list but hosts no
          account operations; the Customer accounts cover becomes a link-through."
          The book is a section of the Homes tab of `admin/NursingHomes.tsx` now,
          which is the screen where a home is LINKED to an account, and the quiet
          entry above navigates there. What this screen kept is the way through,
          unchanged in wording and in order: Price lists, the entry, the Agreed
          prices strip grouped per account.
          ⚠️ The four account reads left this file with it (see `load` above) and
          `AccountBook`'s `onBack` became optional, because a section of a rail
          destination has App's single Back control and needs no second one. */}

      {state.kind === 'ready' && view.kind === 'list' && (
        <RateList
          registry={registry}
          service={view.service}
          rates={state.rates}
          initialFilter={view.filter}
          onBack={() => setView({ kind: 'shelf' })}
          onChanged={load}
        />
      )}

      {state.kind === 'ready' && view.kind === 'addons' && (
        <AddonsRules
          registry={registry}
          meta={state.meta}
          onBack={() => setView({ kind: 'shelf' })}
          onChanged={load}
        />
      )}

      {state.kind === 'ready' && view.kind === 'queue' && (
        <QueueView
          queue={state.queue}
          onBack={() => setView({ kind: 'shelf' })}
          onOpenList={(service, filter) => setView({ kind: 'list', service, filter })}
          onPromote={(seed) => { setNotice(null); setView({ kind: 'promote', seed }); }}
          onChanged={load}
        />
      )}

      {state.kind === 'ready' && view.kind === 'promote' && (
        <>
          <div class="book-list-head">
            <button type="button" class="btn btn--quiet" onClick={() => setView({ kind: 'queue' })}>
              Needs a decision
            </button>
            <h2 class="screen-title">Put it on the list</h2>
          </div>
          <PromoteForm
            registry={registry}
            seed={view.seed}
            onCancel={() => setView({ kind: 'queue' })}
            onDone={(ok, message, name) => {
              setNotice({ kind: ok ? 'ok' : 'error', text: message });
              if (ok) {
                load();
                setView({ kind: 'queue' });
              } else if (name != null) {
                // 409 edit-instead: land in the list filtered to the existing
                // row (resolved by the label where one exists).
                // Slug equality, matching the server's 409 key (a typed
                // "Druids-Glen" must resolve "Druids Glen" — the M5 finding).
                const existing = state.rates.find(
                  (r) => r.service === view.seed.service && slugify(r.label) === slugify(name),
                );
                // Cross-operator race (M6 security note): OUR rates may predate
                // the winner's insert — reload so the list actually shows the
                // row the 409 is talking about.
                load();
                setView({ kind: 'list', service: view.seed.service, filter: existing?.label ?? name });
              }
            }}
          />
        </>
      )}
    </div>
  );
}
